import { MAX_UPLOAD_LABEL } from '@form/FormDropzone/constants';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid4 } from 'uuid';
import { PendingUpload } from './types';

/**
 * Uploads dropped files one request each to `endpoint` and tracks their progress. Finished
 * uploads leave the queue, the caller refreshes its list through `onUploaded`.
 */
export const useFileUploads = (endpoint: string, onUploaded: () => void) => {
    const { t } = useTranslation('form');
    const [pending, setPending] = useState<PendingUpload[]>([]);

    const patch = (uid: string, values: Partial<PendingUpload>) =>
        setPending((current) => current.map((entry) => (entry.uid === uid ? { ...entry, ...values } : entry)));

    const upload = useCallback(
        (files: File[]) =>
            files.forEach((file) => {
                const uid = uuid4();
                setPending((current) => [...current, { uid, name: file.name, progress: 0 }]);

                const data = new FormData();
                data.append('file', file);

                axios
                    .post(process.env.NEXT_PUBLIC_API_HOST + endpoint, data, {
                        onUploadProgress: (event) =>
                            event.total && patch(uid, { progress: Math.min(event.loaded / event.total, 0.99) })
                    })
                    .then(() => {
                        setPending((current) => current.filter((entry) => entry.uid !== uid));
                        onUploaded();
                    })
                    .catch((error) =>
                        patch(uid, {
                            // API Gateway answers a 413 itself, without a JSON body
                            error:
                                error.response?.status === 413
                                    ? t('dropzone.tooLarge', { size: MAX_UPLOAD_LABEL })
                                    : error.response?.status === 422
                                      ? (error.response?.data?.message ?? t('dropzone.uploadFailed'))
                                      : t('dropzone.uploadFailed')
                        })
                    );
            }),
        [endpoint, onUploaded, t]
    );

    const dismiss = useCallback(
        (uid: string) => setPending((current) => current.filter((entry) => entry.uid !== uid)),
        []
    );

    return { pending, upload, dismiss };
};
